import { Box, Button, Grid, Stack, TextField } from '@mui/material';

import { useMutation, useQuery } from 'react-query';
import { DashboardOpcSetting } from '../../../types/app/dashboardOpcSetting';
import {
  DashboardOpcButton,
  DashboardOpcButtonDataType,
  DashboardOpcButtonTypes,
} from '../../../types/app/dashboardOpcButton';
import * as api from '../../../api/app/dashboardsOpcButtonApi';
import { ApiError } from '../../../components/core/ApiError/ApiError';
import { Loader } from '../../../components/core/Loader/Loader';
import { useNavigate } from 'react-router-dom';
import { pathFullscreen, paths } from '../../../utils/core/routes';
import { useTranslation } from '../../../contexts/translation';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { ThemeType, useLocalTheme } from '../../../hooks/useLocalTheme';
import { CustomDialog } from '../../../components/core/Dialogs/CustomDialog';
import { useStyles } from '../PerformanceDashboard/Dashboard.styles';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CheckBox } from '../../../components/core/CheckBox/CheckBox';

interface SetNewValueInputs {
  newValue: string;
}

interface Props {
  dataOpcSetting: DashboardOpcSetting;
  assetId: number;
  assetName: string;
  isFullScreen: boolean;
  lang: string;
}

export const DashboardOpcGrid = ({
  dataOpcSetting,
  assetId,
  assetName,
  isFullScreen,
  lang,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);
  const { localTheme } = useLocalTheme();
  const classes = useStyles();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<SetNewValueInputs>();

  const [buttonProcessingIds, setButtonProcessingIds] = useState<number[]>([]);
  const [showNewValueModal, setShowNewValueModal] = useState<DashboardOpcButton | null>(null);

  const {
    data: opcButtons,
    isLoading,
    isError,
  } = useQuery<DashboardOpcButton[]>(['dashboardOpcButtons', dataOpcSetting.dashboardId], () =>
    api.getDashboardOpcButtonList(Number(dataOpcSetting.dashboardId)),
  );

  const { mutate: performButtonClicked } = useMutation(api.performButtonClicked);

  const cells = Array.from({ length: dataOpcSetting.rows * dataOpcSetting.columns });

  const redirectToDashboard = (button: DashboardOpcButton) => {
    if (isFullScreen)
      navigate(
        `${pathFullscreen.DASHBOARD}/${assetId}/${encodeURIComponent(assetName)}${
          paths.DASHBOARD_OPC
        }/${button.linkToDashBoard}/${lang}`,
      );
    else
      navigate(
        `${paths.ASSETS_LIST}/${assetId}/${encodeURIComponent(assetName)}${paths.DASHBOARD_OPC}/${
          button.linkToDashBoard
        }`,
      );
  };

  const handleButton = (button: DashboardOpcButton) => {
    if (button.type === DashboardOpcButtonTypes.LINK_TO_DASHBOARD) redirectToDashboard(button);
    else {
      let requestData: any = {
        id: button.id,
      };

      const _buttonProcessingIds = [...buttonProcessingIds];
      _buttonProcessingIds.push(button.id);
      setButtonProcessingIds([..._buttonProcessingIds]);

      performButtonClicked(requestData, {
        onSuccess: () => {
          enqueueSnackbar(t('page.dashboard.opc-dashboard.performButtonSuccess'), {
            variant: 'success',
          });

          const _buttonProcessingIds = [...buttonProcessingIds.filter((x) => x !== button.id)];
          setButtonProcessingIds([..._buttonProcessingIds]);

          if (button.linkToDashBoard) redirectToDashboard(button);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
          const _buttonProcessingIds = [...buttonProcessingIds.filter((x) => x !== button.id)];
          setButtonProcessingIds([..._buttonProcessingIds]);
        },
      });
    }
  };

  const handleSubmitSetNewValue: SubmitHandler<SetNewValueInputs> = (data: SetNewValueInputs) => {
    if (!showNewValueModal) return;

    let requestData: any = {
      id: showNewValueModal.id,
      newValue:
        data.newValue === '' &&
        showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.BOOLEAN
          ? 'false'
          : data.newValue,
    };

    const _buttonProcessingIds = [...buttonProcessingIds];
    _buttonProcessingIds.push(showNewValueModal.id);
    setButtonProcessingIds([..._buttonProcessingIds]);

    performButtonClicked(requestData, {
      onSuccess: () => {
        enqueueSnackbar(t('page.dashboard.opc-dashboard.performButtonSuccess'), {
          variant: 'success',
        });

        const _buttonProcessingIds = [
          ...buttonProcessingIds.filter((x) => x !== showNewValueModal.id),
        ];
        setButtonProcessingIds([..._buttonProcessingIds]);
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
        const _buttonProcessingIds = [
          ...buttonProcessingIds.filter((x) => x !== showNewValueModal.id),
        ];
        setButtonProcessingIds([..._buttonProcessingIds]);
      },
    });

    setShowNewValueModal(null);
  };

  const handleSetNewValueButton = (button: DashboardOpcButton) => {
    setShowNewValueModal(button);
    reset({
      newValue: '',
    });
  };

  useEffect(() => {
    setIsDarkTheme(localTheme === ThemeType.DARK);
  }, [localTheme]);

  if (isError) return <ApiError />;
  if (isLoading) return <Loader />;
  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${dataOpcSetting.columns}, minmax(100px, 1fr))`,
            gap: 1,
            minWidth: dataOpcSetting.columns * 100, // aby se spustil horizontální scroll na menších obrazovkách
          }}
        >
          {cells.map((_, index) => {
            const row = Math.floor(index / dataOpcSetting.columns);
            const col = index % dataOpcSetting.columns;

            const opcButton = opcButtons?.find((x) => x.column === col && x.row === row);

            return (
              <Box
                key={index}
                sx={{
                  borderRadius: 1,
                  padding: 2,
                  minHeight: 60,
                  backgroundColor: isDarkTheme ? '#383838' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isDarkTheme ? undefined : 'solid 1px #E98300',
                }}
              >
                {opcButton ? (
                  <Button
                    type="button"
                    variant="contained"
                    color={opcButton.style}
                    onClick={() =>
                      opcButton.type === DashboardOpcButtonTypes.SET_NEW_VALUE
                        ? handleSetNewValueButton(opcButton)
                        : handleButton(opcButton)
                    }
                    style={{
                      color: 'white',
                      fontSize: opcButton.fontSize ? `${opcButton.fontSize}px` : '15px',
                    }}
                    disabled={!!buttonProcessingIds.find((x) => x === opcButton.id)}
                  >
                    {opcButton.name}
                  </Button>
                ) : (
                  <></>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {showNewValueModal && (
        <CustomDialog
          open
          title={showNewValueModal.name}
          titleClass={classes.assetModalCaption}
          maxWidth={'xs'}
          onClose={() => setShowNewValueModal(null)}
        >
          <form onSubmit={handleSubmit(handleSubmitSetNewValue)} noValidate>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.STRING && (
                  <TextField
                    fullWidth
                    label={t('page.dashboard.opc-dashboard.newValue')}
                    {...register('newValue', {
                      required: t('page.dashboard.opc-dashboard.newValueRequired'),
                    })}
                    error={Boolean(errors.newValue)}
                    helperText={errors.newValue?.message}
                  />
                )}
                {(showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.DOUBLE ||
                  showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.FLOAT) && (
                  <TextField
                    fullWidth
                    type="number"
                    InputProps={{ inputProps: { min: 0.0, step: '0.1' } }}
                    label={t('page.dashboard.opc-dashboard.newValue')}
                    {...register('newValue', {
                      required: t('page.dashboard.opc-dashboard.newValueRequired'),
                    })}
                    error={Boolean(errors.newValue)}
                    helperText={errors.newValue?.message}
                  />
                )}
                {(showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.LONG ||
                  showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.INT) && (
                  <TextField
                    fullWidth
                    label={t('page.dashboard.opc-dashboard.newValue')}
                    inputProps={{ step: 1 }}
                    {...register('newValue', {
                      required: t('page.dashboard.opc-dashboard.newValueRequired'),
                      validate: (value) =>
                        Number.isInteger(Number(value)) ||
                        t('page.dashboard.opc-dashboard.newValueWholeNumberRequired'),
                    })}
                    error={Boolean(errors.newValue)}
                    helperText={errors.newValue?.message}
                    type="number"
                  />
                )}
                {showNewValueModal.opcValueDataType === DashboardOpcButtonDataType.BOOLEAN && (
                  <CheckBox
                    label={t('page.dashboard.opc-dashboard.newValue')}
                    controllerProps={{
                      name: 'newValue',
                      control: control,
                    }}
                    errors={errors}
                  />
                )}
              </Grid>

              <Grid item>
                <Stack spacing={2} direction="row">
                  <Button type="submit" variant="contained" color="success">
                    {t('general.action-buttons.save')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </CustomDialog>
      )}
    </>
  );
};
